import classNames from 'classnames';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Customer, CustomerFormFieldErrors } from '../../interfaces/Customer';
import { Stylesheet } from '../../interfaces/Stylesheet';
import { SaveStatus } from '../../pages/Customer/interfaces/Customer';
import Alert from '../Alert/Alert';
import FormElement, { FormChangeEvent } from '../Form/FormElement';
import style from './PersonData.module.scss';

export interface PersonDataProps {
  customer: Customer;
  fieldErrors: CustomerFormFieldErrors;
  isLoading: boolean;
  onFieldChange: <T extends keyof Customer>(value: Customer[T], fieldName: T) => void;
  saveStatus: SaveStatus;
  stylesheet: Stylesheet;
}

const PersonData: FC<PersonDataProps> = ({ stylesheet, customer, onFieldChange, isLoading, saveStatus, fieldErrors }) => {
  const { t } = useTranslation();
  const { initials, middleName, lastName, phoneNumber, mobileNumber, email } = customer;

  const onChange = (event: FormChangeEvent): void => {
    onFieldChange(event.target.value, event.target.name as keyof Customer);
  };

  const isDisabled = (field: string): boolean => {
    return stylesheet.customerSection.editFields.indexOf(field) === -1;
  };

  const preFormExplanation = stylesheet.customerSection.preForm?.value;
  const containerClasses = classNames('col-xs-12 col-xl-6', style.container);

  return (
    <div className={containerClasses}>
      <h2>{t('form:personalDetails')}</h2>
      {preFormExplanation && (<p>{preFormExplanation}</p>)}
      <form className={style.form}>
        <FormElement
          disabled={isDisabled('initials')}
          label={t('form:initials')}
          name="initials"
          onChange={onChange}
          split
          type="text"
          value={initials || ''}
        />
        <FormElement
          disabled={isDisabled('middleName')}
          label={t('form:middleName')}
          name="middleName"
          onChange={onChange}
          split
          type="text"
          value={middleName || ''}
        />
        <FormElement
          disabled={isDisabled('lastName')}
          label={t('form:lastName')}
          name="lastName"
          onChange={onChange}
          type="text"
          value={lastName || ''}
        />
        <FormElement
          disabled={isDisabled('phoneNumber')}
          error={fieldErrors.phoneNumber}
          label={t('form:phoneNumber')}
          name="phoneNumber"
          onChange={onChange}
          split
          type="text"
          value={phoneNumber || ''}
        />
        <FormElement
          disabled={isDisabled('mobileNumber')}
          error={fieldErrors.mobileNumber}
          label={t('form:mobileNumber')}
          name="mobileNumber"
          onChange={onChange}
          split
          type="text"
          value={mobileNumber || ''}
        />
        <FormElement
          disabled={isDisabled('email')}
          error={fieldErrors.email}
          label={t('form:email')}
          name="email"
          onChange={onChange}
          type="text"
          value={email || ''}
        />
      </form>

      {!!(isLoading || saveStatus.success || saveStatus.error) && (
        <Alert
          level={
            isLoading
              ? 'info'
              : saveStatus.error
                ? 'error'
                : 'success'
          }
          text={isLoading ? t('alert:savingDetails') : saveStatus.error ? t('alert:saveError') : t('alert:saveSuccess')}
        />
      )}
    </div>
  );
};

export default PersonData;
